import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Package } from 'lucide-react';
import '../styles/MasterForms.css';
import apiClient from '../services/api';

const ContainerType = () => {
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  const [containertypes, setContainerTypes] = useState([]);
  const [filteredContainerTypes, setFilteredContainerTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [activeOnly, setActiveOnly] = useState(false);
  
  const [formData, setFormData] = useState({
    ContainerTypeID: null,
    ContainerType: '',
    IsActive: true
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!companyId || !yearId) {
      setLoading(false);
      return;
    }
    fetchContainerTypes();
  }, [companyId, yearId, activeOnly]);

  useEffect(() => {
    filterContainerTypes();
  }, [searchTerm, containertypes]);

  const fetchContainerTypes = async () => {
    setLoading(true);
    try {
      const params = activeOnly ? { table: 'containertype', active: '1' } : { table: 'containertype' };
      const response = await apiClient.get('/LookupMaster.php', { params });
      
      if (response.data.success) {
        setContainerTypes(response.data.data || []);
      } else {
        alert('Failed to fetch containertypes');
      }
    } catch (error) {
      console.error('Error fetching containertypes:', error);
      alert('Error fetching containertypes');
    } finally {
      setLoading(false);
    }
  };

  const filterContainerTypes = () => {
    if (!searchTerm.trim()) {
      setFilteredContainerTypes(containertypes);
    } else {
      const filtered = containertypes.filter(containertype =>
        containertype.ContainerType.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredContainerTypes(filtered);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.ContainerType.trim()) {
      newErrors.ContainerType = 'ContainerType name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setSubmitLoading(true);
    try {
      const payload = {
        ...formData,
        IsActive: formData.IsActive ? 1 : 0
      };
      
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/LookupMaster.php?table=containertype', payload);
      
      if (response.data.success) {
        alert(editMode ? 'ContainerType updated successfully!' : 'ContainerType created successfully!');
        setShowModal(false);
        resetForm();
        fetchContainerTypes();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error saving containertype:', error);
      alert('Error saving containertype: ' + (error.response?.data?.message || error.message));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = async (containertype) => {
    try {
      const response = await apiClient.get(`/LookupMaster.php?table=containertype&id=${containertype.ContainerTypeID}`);
      if (response.data.success) {
        setFormData({
          ContainerTypeID: response.data.data.ContainerTypeID,
          ContainerType: response.data.data.ContainerType,
          IsActive: response.data.data.IsActive === 1
        });
        setEditMode(true);
        setShowModal(true);
        setErrors({});
      }
    } catch (error) {
      console.error('Error loading containertype:', error);
      alert('Error loading containertype details');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this containertype?')) return;
    
    try {
      const response = await apiClient.delete('/LookupMaster.php?table=containertype', {
        data: { ContainerTypeID: id }
      });
      
      if (response.data.success) {
        alert('ContainerType deleted successfully!');
        fetchContainerTypes();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error deleting containertype:', error);
      alert('Error deleting containertype');
    }
  };

  const resetForm = () => {
    setFormData({ ContainerTypeID: null, ContainerType: '', IsActive: true });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  if (!companyId || !yearId) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>Container Type Master</h2>
          <p className="subtitle">Manage container types and sizes</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Container Type
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search containertypes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        
        <div className="filter-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
            />
            <span>Active Only</span>
          </label>
        </div>

        <div className="toolbar-info">
          Total: {filteredContainerTypes.length}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading containertypes...</div>
      ) : filteredContainerTypes.length === 0 ? (
        <div className="empty-state">
          <p>No containertypes found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Container Type</th>
                <th>Status</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredContainerTypes.map((containertype) => (
                <tr key={containertype.ContainerTypeID}>
                  <td className="font-mono">{containertype.ContainerTypeID}</td>
                  <td className="font-semibold">{containertype.ContainerType}</td>
                  <td>
                    {containertype.IsActive === 1 ? (
                      <span className="status-badge active">Active</span>
                    ) : (
                      <span className="status-badge inactive">Inactive</span>
                    )}
                  </td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(containertype)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(containertype.ContainerTypeID)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Container Type' : 'Add New Container Type'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-section">
                <h4>
                  <Package size={18} />
                  Container Type Information
                </h4>
                
                <div className="form-group">
                  <label htmlFor="ContainerType">
                    Container Type <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="ContainerType"
                    value={formData.ContainerType}
                    onChange={(e) => setFormData({ ...formData, ContainerType: e.target.value })}
                    maxLength="255"
                    placeholder="e.g., 20ft Standard, 40ft High Cube"
                    className={errors.ContainerType ? 'error' : ''}
                    autoFocus
                  />
                  {errors.ContainerType && (
                    <span className="error-message">{errors.ContainerType}</span>
                  )}
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.IsActive}
                      onChange={(e) => setFormData({ ...formData, IsActive: e.target.checked })}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContainerType;